"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useCountInBeats } from "@/hooks/journey/use-count-in-beats";
import { useRhythmRun } from "@/hooks/journey/use-rhythm-run";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import { useRunKeyboardShortcuts } from "@/hooks/journey/use-run-keyboard-shortcuts";
import { useTrace } from "@/hooks/journey/use-trace";
import { useStudio } from "@/contexts/studio-context";
import type {
  JourneySnapshot,
  LearningJourney,
} from "@/lib/learning-journey/journey";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type {
  Phrase,
  PhraseFSMSnapshot,
} from "@/lib/learning-journey/types";
import { junctionWindow } from "@/lib/learning-journey/work-units";
import type { SongControler } from "@/lib/track/controler/song-controler";
import {
  stallLine,
  afterLanding,
  afterMiss,
  arrivalTitle,
  barOfTick,
  bpmAt,
  handsAsked,
  holesLine,
  interruptionLine,
  landedPass,
  pulseLine,
  rhythmGroups,
  startingRate,
  stepRate,
  strayLine,
  NEW_RUNG,
  RATE_LADDER,
  type RunReading,
  type RungStory,
} from "../rhythm";
import { sustainByGroup } from "../practice";
import { FloatingHeader } from "../shell/floating-header";
import { FloatingShell } from "../shell/floating-shell";
import { MiniSteps } from "../shell/mini-steps";
import {
  OFFER_CLASS,
  OFFER_INLINE_STYLE,
  ACT_FILL,
  PANEL_PADDING_X_PX,
  PANEL_WIDTH_PX,
} from "../shell/panel-metrics";
import { JOURNEY_HINT_COLOR_STRAY } from "@/hooks/journey/journey-hint-mapping";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import type { Hint } from "@/contexts/hint-context";
import { HandsOnHint } from "../shell/hands-on-hint";
import { ACT_H_PX, CompletedFeedback } from "../shell/run-controls";
import { RunMap, type RunSpan } from "../shell/run-map";
import { TraceBand } from "../shell/trace-band";
import { RunningBand } from "./rhythm-state";

/**
 * View for `S5`: the seam between the previous unit and this one.
 *
 * Same machinery and same grammar as the run in time (`rhythm-state.tsx`):
 * one pass, no gate, two panel heights, the pulse on the roll. Two things are
 * its own.
 *
 * **What it plays is the pair, whole.** It used to play a window cut around
 * the seam, which saved length and cost something no other step charges: it
 * dropped the player into the middle of the previous unit, at an entry point
 * they had never rehearsed. See `junctionWindow` for why the length it was
 * saving was not worth saving at this step.
 *
 * **What tempo it plays at.** The spec asks for "le tempo le plus lent validé
 * parmi les deux phrases concernées", and the view used to pin itself to the
 * global `TEMPO_S4_FRACTION` instead, because nothing tracked a tempo per
 * unit. Something does now (`LearningJourney.unitRate`), so a player who had
 * to drop to 40% to hold a dense unit is no longer sent back to a speed they
 * never held, on the passage they know least. The screen says so, once —
 * otherwise the chaining step appears to slow down for no reason.
 */

const LEAD_BOX_H_PX = 34;

/** The order at the top of the panel. Same scale as the steps before. */
const HERO_FONT_PX = 38;

type ChainStateProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly phraseLabel: string;
  readonly journey: LearningJourney;
  readonly snapshot: JourneySnapshot;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  /**
   * Full phrase plan, indexed identically to `snapshot.phraseProgress`,
   * so we can resolve the previous phrase by `currentPhraseIndex - 1`.
   */
  readonly phrases: readonly Phrase[];
};

/** One slot in the hint layer; re-publishing replaces it in place. */
const STRAY_HINT_SOURCE = "journey-chain-stray";

export function ChainState({
  phrase,
  phraseSnapshot,
  journey,
  snapshot,
  rangeRunner,
  songControler,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
  phrases,
}: ChainStateProps) {
  const titleId = useId();
  const index = snapshot.currentPhraseIndex;
  const previous = phrases[index - 1] ?? null;

  const chainRange = useMemo(
    () => junctionWindow(previous, phrase),
    [previous, phrase],
  );
  const groups = useMemo(
    () => rhythmGroups(chainRange),
    [chainRange],
  );
  // A hand that holds is a hand that is playing: the rail lights it, and the
  // score is what says so. See `sustainByGroup`.
  const sustaining = useMemo(
    () => sustainByGroup(chainRange, groups),
    [chainRange, groups],
  );

  // ── Tempo ────────────────────────────────────────────────────

  const openingRate = useMemo(() => {
    const fallback = startingRate(groups, bpm, ppq);
    const rates = [index - 1, index]
      .map((i) => journey.unitRate(i))
      .filter((r): r is number => r !== null);

    if (rates.length === 0) {
      return fallback;
    }

    return Math.min(...rates);
  }, [journey, index, groups, bpm, ppq]);

  const [rate, setRate] = useState(openingRate);
  const [rateUnit, setRateUnit] = useState(phrase.id);

  const effectiveBpm = bpmAt(bpm, rate);

  // ── The pass ─────────────────────────────────────────────────

  const [passes, setPasses] = useState(0);
  const [story, setStory] = useState<RungStory>(NEW_RUNG);
  /** Keys struck by the pass that just settled. See the same state in `rhythm-state`. */
  const [strikes, setStrikes] = useState(0);

  // Everything on this screen is about one junction. Adjusted during render
  // rather than in an effect: the frame an effect would let through is the
  // previous junction's tempo and the previous junction's repetitions, shown
  // as this one's. See `runSubject` for the reading, which the run drops on
  // the same change.
  if (rateUnit !== phrase.id) {
    setRateUnit(phrase.id);
    setRate(openingRate);
    setPasses(0);
    setStory(NEW_RUNG);
    setStrikes(0);
  }

  const { studio } = useStudio();

  const handleFinished = useCallback(
    (finished: { reading: RunReading }) => {
      setStrikes(studio?.liveStrikeCount() ?? 0);

      if (finished.reading.interruptions.length > 0) {
        setStory(afterMiss);

        return;
      }

      // Same bar as the unit in time: crossing the junction **with the
      // notes**. See `landedPass`.
      if (!landedPass(finished.reading)) {
        setStory(afterMiss);

        return;
      }

      setPasses((n) => n + 1);
      setStory(afterLanding);
    },
    [studio],
  );

  const run = useRhythmRun({
    journey,
    rangeRunner,
    range: chainRange,
    groups,
    bpm,
    ppq,
    ticksPerBar,
    rate,
    state: "S5",
    auditionSong: false,
    accompanyHand: null,
    songControler,
    onFinished: handleFinished,
  });

  const reading = run.lastRun?.reading ?? null;
  const wentThrough = reading !== null && reading.interruptions.length === 0;
  // No reading is not "dirty": before a pass there is nothing to hold back.
  const clean = reading === null || landedPass(reading);
  /** What the pass fell short by, or nothing. See the same line in `rhythm-state`. */
  const shortfall: string | null =
    reading === null ? null : (strayLine(reading) ?? holesLine(reading));

  /**
   * The key just struck for nothing, lit red under the finger that struck it.
   *
   * S5 publishes no hint of its own — the step is played from memory — so the
   * slot is free and holds nothing but this. Retracted as soon as the flash
   * is over, which is what `null` does here.
   */
  const strayHint = useMemo<Hint | null>(
    () =>
      run.strays.flashing.length === 0
        ? null
        : {
            source: STRAY_HINT_SOURCE,
            midis: run.strays.flashing.map((midi) => ({
              midi,
              color: JOURNEY_HINT_COLOR_STRAY,
            })),
          },
    [run.strays.flashing],
  );

  useHintPublisher(strayHint);
  const countIn = useCountInBeats();

  // ── The trace ────────────────────────────────────────────────

  /** Where the reading of the pass begins. See the same memo in `rhythm-state`. */
  const traceParkTick = useMemo(() => {
    if (reading === null) {
      return null;
    }

    const stop = reading.interruptions[0];

    if (stop !== undefined) {
      return Math.max(chainRange.startTick, stop.startTick - ticksPerBar);
    }

    const hole = reading.honoured.indexOf(false);

    if (hole >= 0 && groups[hole] !== undefined) {
      return Math.max(chainRange.startTick, groups[hole].startTick - ticksPerBar);
    }

    // The seam and not the top: this step exists for the joint between two
    // passages that are each already fluent, and it is the only place on the
    // range worth opening a reading on.
    return Math.max(chainRange.startTick, phrase.startTick - ticksPerBar);
  }, [reading, chainRange.startTick, phrase.startTick, ticksPerBar, groups]);

  const trace = useTrace(traceParkTick);
  const traceHide = trace.hide;
  const canTrace = reading !== null && strikes > 0;

  const handleStart = useCallback(() => {
    // Before the pass: the lane is emptied when a run is armed.
    traceHide();
    run.clear();
    run.start();
  }, [run, traceHide]);

  const handleContinue = useCallback(() => {
    journey.completeRhythmRun("validated");
  }, [journey]);

  /**
   * Changing the tempo plays the junction at it. See the same ref in
   * `rhythm-state`: as a setting the offer looked broken, because it left
   * the player in front of the screen they had just asked to leave.
   */
  const playAfterRate = useRef(false);

  const handleStepTempo = useCallback((direction: -1 | 1) => {
    playAfterRate.current = true;
    setRate((current) => stepRate(current, direction));
  }, []);

  useEffect(() => {
    if (!playAfterRate.current) {
      return;
    }

    playAfterRate.current = false;
    handleStart();
  }, [rate, handleStart]);

  useRunKeyboardShortcuts({
    status: run.status,
    hasFeedback: run.lastRun !== null,
    onStart: handleStart,
    onRetry: handleStart,
    onCancel: run.cancel,
  });

  // ── Stepper ──────────────────────────────────────────────────

  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );
  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  // ── Drawing ──────────────────────────────────────────────────

  const span = useMemo<RunSpan>(
    () => ({
      startTick: chainRange.startTick,
      endTick: chainRange.endTick,
      ticksPerBeat,
      ticksPerBar,
    }),
    [chainRange, ticksPerBeat, ticksPerBar],
  );

  const running = run.status === "running";
  const handsOn = useHandsOnPiano();

  // Same rule as the unit in time: the pedal launches, and so does the
  // junction's opening group until a pass has happened. See `rhythm-state`.
  // Any key launches, including before the first pass — unlike the unit in
  // time. There the opening group is asked for because the panel is showing a
  // keyboard to be read, and a player trying a key against that map has not
  // asked for a clock. This panel shows no keyboard, so the same rule would
  // gate the start on a note nothing on screen points at.
  useHandsOnControls({
    enabled: !running && !wentThrough && !trace.open,
    onAdvance: handleStart,
    onPlay: handleStart,
    playMidis: null,
  });

  const phraseNumber = index + 1;
  const previousNumber = phraseNumber - 1;
  const bars = Math.max(
    1,
    Math.ceil((chainRange.endTick - chainRange.startTick) / ticksPerBar),
  );
  const currentBar =
    barOfTick(run.playedTick ?? chainRange.startTick, chainRange.startTick, ticksPerBar) ?? 1;

  const kicker =
    previous === null
      ? `ENCHAÎNEMENT · PHRASE ${phraseNumber}`
      : `ENCHAÎNEMENT · PHRASES ${previousNumber} → ${phraseNumber}`;

  /**
   * The hands the junction really asks for, over both phrases it joins.
   *
   * The band used to light both pills unconditionally. On a passage where
   * one hand never plays a note, that is the plainest kind of untrue a step
   * can say about itself — the reason `handsAsked` exists.
   */
  const asked = handsAsked({
    notesByHand: {
      left: [
        ...(previous?.notesByHand.left ?? []),
        ...phrase.notesByHand.left,
      ],
      right: [
        ...(previous?.notesByHand.right ?? []),
        ...phrase.notesByHand.right,
      ],
    },
  });

  const faster = wentThrough && clean;
  // Went to the end, but not under the right keys: the rung is withheld and
  // said so. See `Offers` in `rhythm-state` — the two steps hold the same
  // line, and a ladder that could be climbed on wrong notes in one of them
  // would make it a detour rather than a rule.
  const tempoHeld = wentThrough && !clean;
  const atLadderEnd = faster
    ? rate >= RATE_LADDER[RATE_LADDER.length - 1]
    : rate <= RATE_LADDER[0];

  /**
   * Once the junction runs, the panel is the band the unit in time uses.
   *
   * Literally the same component: the two steps are the same act on two
   * ranges, and the player crosses them one after the other in the same
   * minute. Two hand-written rows that agreed only by copy would have
   * drifted at the first change to either.
   */
  if (running) {
    return (
      <FloatingShell
        variant="band"
        ariaLabel="Enchaînement de phrases"
        titleId={titleId}
        contentKey="chain-band"
      >
        <RunningBand
          titleId={titleId}
          kicker={kicker}
          bpm={effectiveBpm}
          percent={
            groups.length < 2
              ? 0
              : (Math.min(run.playedIndex, groups.length - 1) /
                  (groups.length - 1)) *
                100
          }
          mode={asked}
          playerHand={null}
          countIn={countIn}
          currentBar={currentBar}
          bars={bars}
          strays={run.strays.count}
          onCancel={run.cancel}
        />
      </FloatingShell>
    );
  }

  return (
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel="Enchaînement de phrases"
      titleId={titleId}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, stepIndex)}
        stepTotal={steps.length}
        kicker={kicker}
        titleId={titleId}
        trailing={
          <span
            className="font-mono tabular-nums shrink-0"
            style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
          >
            {effectiveBpm} BPM
          </span>
        }
      />

      {trace.open ? (
        <TraceBand
          stopBar={
            reading?.interruptions[0] === undefined
              ? null
              : barOfTick(
                  reading.interruptions[0].startTick,
                  chainRange.startTick,
                  ticksPerBar,
                )
          }
          // A junction is a handful of bars either side of the seam, and the
          // park has just put the seam under the playhead.
          hint={null}
          onClose={trace.hide}
        />
      ) : (
      <div style={{ padding: `26px ${PANEL_PADDING_X_PX}px 14px` }}>
        {/*
          The order first and at the scale of the steps before it. It used to
          sit under the two cards, at 13px: the one sentence saying what to do
          read as a caption of the thing it was about, on the only step of the
          parcours that had not been through the pass that made the others
          open on their order.
        */}
        <p
          style={{
            margin: 0,
            fontSize: HERO_FONT_PX,
            lineHeight: 1.08,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "var(--color-text-primary)",
            textWrap: "balance",
          }}
        >
          {orderLine(previous !== null)}
        </p>

        <div
          style={{
            minHeight: LEAD_BOX_H_PX,
            marginTop: 10,
            // Air under a line that can wrap, so the cards it introduces
            // never touch it.
            marginBottom: 6,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.45,
              color: "var(--color-text-secondary)",
              textWrap: "pretty",
            }}
          >
            {run.stalled !== null && run.lastRun === null
              ? stallLine(run.stalled)
              : (noteLine(
                  run.lastRun === null,
                  passes,
                  previous !== null,
                  effectiveBpm,
                  reading === null || reading.interruptions.length === 0
                    ? null
                    : interruptionLine(
                        reading.interruptions[0],
                        chainRange.startTick,
                        ticksPerBar,
                      ),
                ) ?? "")}
          </p>
        </div>

        <Junction
          previous={previous}
          current={phrase}
          phraseNumber={phraseNumber}
          previousNumber={previousNumber}
        />

        {/* The map follows the cards now, and needs the air the line above
            used to give it. */}
        <div style={{ marginTop: 14 }}>
          <RunMap
            groups={groups}
            hands={["R", "L"]}
            span={span}
            honoured={reading?.honoured}
            sustaining={sustaining}
          />
        </div>

        {/* Under the map, for the reason given in `rhythm-state`. */}
        {canTrace && (
          <div className="flex justify-end" style={{ height: 16 }}>
            <button
              type="button"
              onClick={trace.show}
              className={`${OFFER_CLASS} shrink-0`}
              style={OFFER_INLINE_STYLE}
            >
              Voir ce que tu as joué
            </button>
          </div>
        )}

        {wentThrough ? (
          <CompletedFeedback
            passes={passes}
            onRetry={handleStart}
            onContinue={handleContinue}
            handColor={ACT_FILL}
            // Under the map and the trace offer: see `both-hands-state`.
            divided
            title={arrivalTitle(
              story,
              // A junction with nothing before it is the unit on its own, and
              // « les deux se suivent » would name a second one that is not
              // there.
              previous === null ? "Tu es allé au bout." : "Les deux se suivent.",
            )}
            // Held back on the pass that lands after several that did not:
            // see the same card in `rhythm-state`. The pulse line stays, it
            // never says anything but good news or nothing.
            note={
              story.salute
                ? pulseLine(reading?.pulse ?? null)
                : (shortfall ?? pulseLine(reading?.pulse ?? null))
            }
          />
        ) : (
          <>
            {/*
              The act the card offers, at the size the card offers it. This
              was a white strip across the panel at 12px: the same act —
              play a pass — drawn one way before the run and another on the
              card that follows it, on a step the player crosses four times
              in a row. The arrow goes with it; « Encore une fois » starts
              the same pass and carries none.
            */}
            <button
              type="button"
              onClick={handleStart}
              className="inline-flex items-center justify-center transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
              style={{
                minHeight: ACT_H_PX,
                padding: "0 26px",
                borderRadius: 10,
                border: "none",
                background: ACT_FILL,
                color: "#0d0d12",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {run.lastRun !== null
                ? "Reprendre"
                : previous === null
                  ? "Jouer"
                  : "Jouer l’enchaînement"}
            </button>

            {handsOn && (
              <HandsOnHint
                items={[
                  {
                    gesture: run.lastRun === null ? "Joue" : "Rejoue",
                    effect:
                      run.lastRun === null
                        ? "et le décompte part"
                        : "reprendre",
                  },
                  { gesture: "Pédale", effect: "lancer" },
                ]}
              />
            )}
          </>
        )}

        {tempoHeld ? (
          <span
            style={{
              display: "inline-block",
              marginTop: 10,
              fontSize: 11,
              color: "var(--color-text-secondary)",
            }}
          >
            Le tempo monte quand l&apos;enchaînement sort juste.
          </span>
        ) : (
          !atLadderEnd && (
          <button
            type="button"
            onClick={() => handleStepTempo(faster ? 1 : -1)}
            className="transition-colors hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            style={{
              marginTop: 10,
              marginLeft: -4,
              padding: "2px 4px",
              borderRadius: 4,
              border: "none",
              background: "transparent",
              color: "var(--color-text-secondary)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {`▶ Jouer plus ${faster ? "vite" : "lentement"} · ${bpmAt(
              bpm,
              stepRate(rate, faster ? 1 : -1),
            )} BPM`}
          </button>
          )
        )}
      </div>
      )}

      <MiniSteps
        active={stepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
        onBack={phraseSnapshot.canStepBack ? handleBack : undefined}
      />
    </FloatingShell>
  );
}

// ── What is being joined ─────────────────────────────────────────

function Junction({
  previous,
  current,
  phraseNumber,
  previousNumber,
}: {
  previous: Phrase | null;
  current: Phrase;
  phraseNumber: number;
  previousNumber: number;
}) {
  if (previous === null) {
    return (
      <PhraseCard
        current
        phraseNumber={phraseNumber}
        startMeasure={current.startMeasure}
        endMeasure={current.endMeasure}
        radius="10px"
      />
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "stretch" }}>
      <PhraseCard
        phraseNumber={previousNumber}
        startMeasure={previous.startMeasure}
        endMeasure={previous.endMeasure}
        radius="10px 0 0 10px"
      />
      <JunctionBand />
      <PhraseCard
        current
        phraseNumber={phraseNumber}
        startMeasure={current.startMeasure}
        endMeasure={current.endMeasure}
        radius="0 10px 10px 0"
      />
    </div>
  );
}

function PhraseCard({
  current = false,
  phraseNumber,
  startMeasure,
  endMeasure,
  radius,
}: {
  current?: boolean;
  phraseNumber: number;
  startMeasure: number;
  endMeasure: number;
  radius: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        padding: 10,
        background: current
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(255, 255, 255, 0.02)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: radius,
      }}
    >
      <div
        className="font-mono uppercase"
        style={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: "0.15em",
          color: "var(--color-text-secondary)",
          marginBottom: 4,
        }}
      >
        Phrase {phraseNumber}
      </div>

      <div
        className="tabular-nums"
        style={{
          fontSize: 16,
          color: current
            ? "var(--color-text-primary)"
            : "var(--color-text-secondary)",
          lineHeight: 1.1,
        }}
      >
        m. {startMeasure}–{endMeasure}
      </div>
    </div>
  );
}

function JunctionBand() {
  return (
    <div
      aria-hidden
      className="flex items-center justify-center"
      style={{
        width: 30,
        flexShrink: 0,
        borderTop: "1px solid var(--color-border-subtle)",
        borderBottom: "1px solid var(--color-border-subtle)",
      }}
    >
      <span
        style={{ fontSize: 16, color: "var(--color-text-secondary)" }}
      >
        ↔
      </span>
    </div>
  );
}

// ── Copy ─────────────────────────────────────────────────────────

/**
 * What the step asks for, in the title.
 *
 * It does not move: a title that changed with the state of the run would be
 * the panel answering in the place it asks from, and the answer already has
 * a card of its own at the foot of the panel.
 */
function orderLine(hasPrevious: boolean): string {
  return hasPrevious
    ? "Passe la jonction sans t’arrêter."
    : "Va au bout sans t’arrêter.";
}

/**
 * What is worth adding under the order, or nothing.
 *
 * The tempo of the junction is stated **once**, before the first pass, and
 * only when it differs from what the units were played at — otherwise the
 * step appears to slow down for no reason. After that the line carries what
 * just happened, and is empty when nothing did: the order above says what to
 * do, and repeating it in grey under itself would be the panel saying one
 * thing twice.
 */
function noteLine(
  first: boolean,
  passes: number,
  hasPrevious: boolean,
  effectiveBpm: number,
  stopped: string | null,
): string | null {
  if (stopped !== null) {
    return stopped;
  }

  if (first && passes === 0) {
    return hasPrevious
      ? `Les deux phrases à la suite, au tempo de la plus lente : ${effectiveBpm} BPM.`
      : `Cette phrase seule, à ${effectiveBpm} BPM.`;
  }

  return null;
}
